/**
 * QueueService - Handles queue and detention management
 */

import { Timestamp, where, orderBy, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { ConsequenceRepository } from '../repositories/ConsequenceRepository';
import { db, functions, httpsCallable } from '../../../firebase';

export class QueueService {
  constructor(consequenceRepo = null) {
    this.consequenceRepo = consequenceRepo || new ConsequenceRepository();
  }

  /**
   * Get queue items with filters
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} Queue items
   */
  async getQueueItems(filters = {}) {
    console.log('[QueueService] 🔍 getQueueItems called with filters:', filters);
    try {
      // Build query filters
      const options = {
        undone: false,
        ...filters
      };

      console.log('[QueueService] 🔧 Built options:', options, 'schoolId:', options.schoolId);

      if (filters.eventType) {
        if (filters.eventType === 'mention') {
          // For mentions, we'd need to query the mention collection
          // This is handled separately in MentionService
          console.log('[QueueService] ⏭️ Skipping mentions - handled separately');
          return [];
        } else {
          options.type = filters.eventType;
        }
      }

      console.log('[QueueService] 📞 Calling getRecentConsequences...');
      const results = await this.consequenceRepo.getRecentConsequences(30, options);
      console.log('[QueueService] ✅ getRecentConsequences returned', results.length, 'items');
      return results;
    } catch (error) {
      console.error('[QueueService] ❌ Error fetching queue items:', error);
      return [];
    }
  }

  /**
   * Subscribe to queue changes
   * @param {Function} callback - Callback for updates
   * @param {Object} filters - Filter options (must include schoolId for hierarchical collections)
   * @returns {Function} Unsubscribe function
   */
  subscribeQueueItems(callback, filters = {}) {
    const constraints = [
      where('undone', '==', false),
      orderBy('issuedAt', 'desc')
    ];

    if (filters.studentId) {
      constraints.unshift(where('studentId', '==', filters.studentId));
    }

    if (filters.classId) {
      constraints.unshift(where('classId', '==', filters.classId));
    }

    if (filters.status) {
      constraints.unshift(where('status', '==', filters.status));
    }

    if (filters.type) {
      constraints.unshift(where('type', '==', filters.type));
    }

    // CRITICAL: Pass schoolId for hierarchical collection access
    return this.consequenceRepo.subscribe(callback, constraints, filters.schoolId);
  }

  /**
   * Update queue item
   * @param {string} itemId - Item ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated item
   */
  async updateQueueItem(itemId, updates) {
    try {
      return await this.consequenceRepo.update(itemId, updates);
    } catch (error) {
      console.error('Error updating queue item:', error);
      throw error;
    }
  }

  /**
   * Record one attendance outcome through the durable server session.
   */
  async actionQueueItem({ schoolId, itemId, action, sessionId }) {
    const callable = httpsCallable(functions, 'updateQueueItem');
    const result = await callable({ schoolId, itemId, action, sessionId });
    return result.data;
  }

  /**
   * Atomically apply one roster outcome to all consequence records represented
   * by a single learner row.
   */
  async updateQueueItems(itemIds, updates) {
    const ids = [...new Set((itemIds || []).filter(Boolean))];
    const schoolId = updates?.schoolId;
    if (!schoolId) throw new Error('schoolId is required for grouped queue updates');
    if (ids.length === 0) throw new Error('At least one queue item is required');
    if (ids.length > 500) throw new Error('Grouped queue update exceeds Firestore batch limit');

    const dataWithoutSchoolId = { ...updates };
    delete dataWithoutSchoolId.schoolId;
    const updateData = {
      ...dataWithoutSchoolId,
      updatedAt: Timestamp.now(),
    };
    const collectionName = this.consequenceRepo?.collectionName;
    if (!collectionName) {
      throw new Error('Queue collection name is required for grouped queue updates');
    }
    const batch = writeBatch(db);
    ids.forEach(itemId => {
      batch.update(
        doc(db, 'schools', schoolId, collectionName, itemId),
        updateData
      );
    });
    await batch.commit();
    return ids.map(id => ({ id, ...updateData }));
  }

  /**
   * Delete queue item and revert constituent mentions
   * @param {string} itemId - Item ID
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<void>}
   */
  async deleteQueueItem(itemId, schoolId) {
    try {
      if (!schoolId) {
        throw new Error('schoolId is required for deleteQueueItem');
      }

      // Get the consequence to find its triggering mentions
      const consequence = await this.consequenceRepo.getById(itemId, schoolId);

      if (consequence && consequence.triggeringMentions && consequence.triggeringMentions.length > 0) {
        // Revert the mentions to unactioned state
        console.log(`Reverting ${consequence.triggeringMentions.length} mentions to unactioned state`);

        for (const mentionId of consequence.triggeringMentions) {
          try {
            // HIERARCHICAL PATH: schools/{schoolId}/mentions
            const mentionRef = doc(db, 'schools', schoolId, 'mentions', mentionId);
            await updateDoc(mentionRef, {
              actioned: false,
              triggeredConsequences: []
            });
            console.log(`✓ Reverted mention ${mentionId}`);
          } catch (mentionError) {
            console.warn(`Failed to revert mention ${mentionId}:`, mentionError);
            // Continue with other mentions even if one fails
          }
        }
      }

      // Delete the consequence
      return await this.consequenceRepo.delete(itemId, schoolId);
    } catch (error) {
      console.error('Error deleting queue item:', error);
      throw error;
    }
  }

  /**
   * Undo queue item
   * @param {string} itemId - Item ID
   * @param {Object} undoDetails - Undo details
   * @returns {Promise<Object>} Updated item
   */
  async undoQueueItem(itemId, undoDetails = {}) {
    try {
      return await this.consequenceRepo.undoConsequence(itemId, {
        ...undoDetails,
        undoneAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error undoing queue item:', error);
      throw error;
    }
  }

  /**
   * Mark detention as served
   * @param {string} detentionId - Detention ID
   * @param {Object} servedDetails - Details about serving
   * @returns {Promise<Object>} Updated detention
   */
  async markDetentionServed(detentionId, servedDetails = {}) {
    try {
      return await this.consequenceRepo.markDetentionServed(detentionId, servedDetails);
    } catch (error) {
      console.error('Error marking detention as served:', error);
      throw error;
    }
  }

  /**
   * Schedule a detention
   * @param {string} detentionId - Detention ID
   * @param {Date} scheduledDate - Scheduled date
   * @param {Object} details - Additional details
   * @returns {Promise<Object>} Updated detention
   */
  async scheduleDetention(detentionId, scheduledDate, details = {}) {
    try {
      const updates = {
        scheduledDate: Timestamp.fromDate(scheduledDate),
        status: 'scheduled',
        ...details
      };

      return await this.consequenceRepo.update(detentionId, updates);
    } catch (error) {
      console.error('Error scheduling detention:', error);
      throw error;
    }
  }

  /**
   * Get detentions for a specific date
   * @param {Date} date - Date to query
   * @param {Object} filters - Additional filters
   * @returns {Promise<Array>} Detentions for date
   */
  async getDetentionsByDate(date, filters = {}) {
    try {
      return await this.consequenceRepo.getDetentionsByDate(date, filters);
    } catch (error) {
      console.error('Error fetching detentions by date:', error);
      return [];
    }
  }

  /**
   * Get queue statistics
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Queue statistics
   */
  async getQueueStatistics(filters = {}) {
    try {
      const items = await this.getQueueItems(filters);

      const stats = {
        total: items.length,
        pending: 0,
        scheduled: 0,
        archived: 0,
        byType: {},
        bySeverity: {}
      };

      items.forEach(item => {
        // Count by status
        if (item.status === 'pending') stats.pending++;
        else if (item.status === 'scheduled') stats.scheduled++;
        else if (item.status === 'archived') stats.archived++;

        // Count by type
        if (!stats.byType[item.type]) stats.byType[item.type] = 0;
        stats.byType[item.type]++;

        // Count by severity
        if (!stats.bySeverity[item.severity]) stats.bySeverity[item.severity] = 0;
        stats.bySeverity[item.severity]++;
      });

      return stats;
    } catch (error) {
      console.error('Error calculating queue statistics:', error);
      return {
        total: 0,
        pending: 0,
        scheduled: 0,
        archived: 0,
        byType: {},
        bySeverity: {}
      };
    }
  }

  /**
   * Add action to queue item
   * @param {string} itemId - Item ID
   * @param {Object} actionDetails - Action details
   * @returns {Promise<Object>} Updated item
   */
  async addActionToItem(itemId, actionDetails) {
    try {
      return await this.consequenceRepo.addAction(itemId, actionDetails);
    } catch (error) {
      console.error('Error adding action to item:', error);
      throw error;
    }
  }

  /**
   * Get pending detentions
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} Pending detentions
   */
  async getPendingDetentions(filters = {}) {
    try {
      const detentionFilters = {
        ...filters,
        type: 'detention',
        status: 'pending'
      };

      return await this.consequenceRepo.getPendingConsequences(detentionFilters);
    } catch (error) {
      console.error('Error fetching pending detentions:', error);
      return [];
    }
  }

  /**
   * Get unserved detentions
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} Unserved detentions
   */
  async getUnservedDetentions(filters = {}) {
    try {
      const today = new Date();
      const detentions = await this.consequenceRepo.getDetentionsByDate(today, {
        ...filters,
        served: false
      });

      // Include past unserved detentions
      const pastDate = new Date();
      pastDate.setDate(pastDate.getDate() - 30); // Look back 30 days

      const pastDetentions = await this.consequenceRepo.getRecentConsequences(30, {
        type: 'detention',
        undone: false
      });

      return pastDetentions.filter(d => {
        if (d.served || d.undone) return false;
        const scheduledDate = d.scheduledDate?.toDate?.() || null;
        return scheduledDate && scheduledDate < today;
      });
    } catch (error) {
      console.error('Error fetching unserved detentions:', error);
      return [];
    }
  }

  /**
   * Batch update queue items
   * @param {Array} updates - Array of {id, updates} objects
   * @returns {Promise<Array>} Updated items
   */
  async batchUpdateItems(updates) {
    try {
      const promises = updates.map(({ id, updates }) =>
        this.consequenceRepo.update(id, updates)
      );

      return await Promise.all(promises);
    } catch (error) {
      console.error('Error batch updating items:', error);
      throw error;
    }
  }
}
